import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getClaimAllStatus,
  getExclusiveItems,
  getLockedRosters,
  getMissionItems,
  getMissionStatuses,
  getRosterItems,
} from '../../services/client/club-store';

import {
  QUERY_KEY_ROSTER,
  QUERY_KEY_EXCLUSIVE_ITEMS,
  QUERY_KEY_MISSION_ITEMS,
  QUERY_KEY_MISSION_CLAIM_STATUS,
  QUERY_KEY_MISSION_PROGRESS_STATUS,
} from '../../utils/constants/query-key';
import {
  useGetCurrentUserId,
  useIsAuthenticated,
  useIsAuthenticating,
} from '@wwe-portal/web/providers/stores/auth-store';

import { MissionStatusResponse } from '@wwe-portal/entities/club-store.type';

export const mapMissionTypeToLabel: Record<number, string> = {
  1: 'Daily',
  2: 'Weekly',
  3: 'Monthly',
  4: 'Yearly',
};

export const reset_interval = 10000;

export const useExclusiveItems = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEY_EXCLUSIVE_ITEMS],
    queryFn: async () => {
      const { data } = await getExclusiveItems();
      return data.data;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return {
    data,
    ...rest,
  };
};

export const useInvalidateExclusiveItems = () => {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_EXCLUSIVE_ITEMS],
    });
  };
};

export const useMissionItems = () => {
  const { data: missions } = useQuery({
    queryKey: [QUERY_KEY_MISSION_ITEMS],
    queryFn: async () => {
      const response = await getMissionItems();
      return response.data.data;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  if (!missions) return { data: [] };

  return { missions };
};

export const useClaimAllStatus = () => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  const { data: missionItemResponse } = useQuery({
    queryKey: [QUERY_KEY_MISSION_CLAIM_STATUS, { userId, isAuthenticated }],
    queryFn: getClaimAllStatus,
    enabled: isAuthenticated,
  });
  const data = missionItemResponse?.data;

  return { data };
};

export const useInvalidateClaimAllStatus = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_MISSION_CLAIM_STATUS],
    });
};

export const useMissionStatus = () => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  const { data: missionStatusesResponse } = useQuery({
    queryKey: [QUERY_KEY_MISSION_PROGRESS_STATUS, { userId, isAuthenticated }],

    queryFn: async () => {
      if (!isAuthenticated) return null;
      return await getMissionStatuses();
    },

    refetchInterval: reset_interval,
  });

  const data: Record<string, MissionStatusResponse> | undefined =
    missionStatusesResponse?.data?.data.reduce(
      (list, status: MissionStatusResponse) => ({
        ...list,
        [status.milestoneId]: status,
      }),
      {}
    );

  return { data };
};

export const useRosterItems = () => {
  const isAuthenticated = useIsAuthenticated();
  const isAuthenticating = useIsAuthenticating();
  const userId = useGetCurrentUserId();
  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEY_ROSTER, 'unlocked', { isAuthenticated, userId }],
    queryFn: async () => {
      if (!isAuthenticated) return null;
      return getRosterItems();
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: isAuthenticated,
  });

  const total = data?.data.data.length;
  return { data, total, isLoading: isAuthenticating || isLoading };
};

export const useLockedRosters = () => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [QUERY_KEY_ROSTER, 'locked', { isAuthenticated, userId }],

    queryFn: async () => {
      if (!isAuthenticated) return null;
      return getLockedRosters();
    },

    refetchOnWindowFocus: false,
  });
  const total = data?.data.data.length;
  return { data, total, isInitialLoading: isLoading, isFetching };
};

export const useInvalidateRosterItems = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_ROSTER],
    });
};
